import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { StatusDotPill } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { SpareCase, SpareCasePipelineCount } from './useData';
import { fillSpareCaseReferenceFromTemplate, scheduleSpareCaseReferencePeek } from './referencePrefill';

/** Refresh Reference from SPARE_CASE template when date / vessel / categories change. */
const spareCaseReferencePeekSideEffect: FieldConfig['onChangeSideEffect'] = (
  _value,
  _setFieldValue,
  setMultipleFieldValues,
  getFormData,
) => {
  if (!getFormData) return;
  scheduleSpareCaseReferencePeek(setMultipleFieldValues, getFormData);
};

const PIPELINE_STAGES: { countKey: keyof SpareCasePipelineCount; label: string; short: string }[] = [
  { countKey: 'requisitions', label: 'Requisition', short: 'Req' },
  { countKey: 'enquiries', label: 'RFQ', short: 'RFQ' },
  { countKey: 'quotations', label: 'Quotation', short: 'Quote' },
  { countKey: 'evaluations', label: 'Evaluation', short: 'Eval' },
  { countKey: 'purchaseOrders', label: 'Order', short: 'Order' },
  { countKey: 'deliveries', label: 'Delivery', short: 'Dlv' },
  { countKey: 'invoices', label: 'Invoice', short: 'Inv' },
];

/** List column headers (REQ / ENQ / …) for the per-stage count columns. */
const STAGE_COLUMN_HEADER: Record<keyof SpareCasePipelineCount, string> = {
  requisitions: 'REQ',
  enquiries: 'ENQ',
  quotations: 'QUO',
  evaluations: 'EVAL',
  purchaseOrders: 'ORD',
  deliveries: 'DLV',
  invoices: 'INV'};

/** Suggested action for the first pipeline stage with no records yet. */
const NEXT_STEP_MESSAGE: Record<keyof SpareCasePipelineCount, string> = {
  requisitions: 'Add requisition(s) for this case',
  enquiries: 'Proceed with RFQ to suppliers',
  quotations: 'Request supplier quotations',
  evaluations: 'Run quotation evaluation',
  purchaseOrders: 'Create purchase order(s)',
  deliveries: 'Record delivery',
  invoices: 'Create invoice(s)'};

function getPipelineStageStates(
  counts: SpareCasePipelineCount | undefined,
): {
  stages: Array<
    (typeof PIPELINE_STAGES)[number] & { hasActivity: boolean; isNext: boolean; count: number; isNotNeeded: boolean }
  >;
  allDone: boolean;
  nextLabel: string | null;
  nextCountKey: keyof SpareCasePipelineCount | null;
} {
  const c: SpareCasePipelineCount = counts ?? {
    requisitions: 0,
    enquiries: 0,
    quotations: 0,
    evaluations: 0,
    purchaseOrders: 0,
    deliveries: 0,
    invoices: 0};
  const evaluationSkippedByDirectOrder = c.evaluations === 0 && c.purchaseOrders > 0;
  let nextIdx: number | null = null;
  for (let i = 0; i < PIPELINE_STAGES.length; i++) {
    const stage = PIPELINE_STAGES[i];
    if (stage.countKey === 'evaluations' && evaluationSkippedByDirectOrder) {
      continue;
    }
    if (c[stage.countKey] === 0) {
      nextIdx = i;
      break;
    }
  }
  const allDone = nextIdx === null;
  const stages = PIPELINE_STAGES.map((s, i) => {
    const count = c[s.countKey];
    return {
      ...s,
      count,
      hasActivity: count > 0,
      isNext: !allDone && i === nextIdx,
      isNotNeeded: s.countKey === 'evaluations' && evaluationSkippedByDirectOrder};
  });
  const nextLabel = allDone ? null : PIPELINE_STAGES[nextIdx!].label;
  const nextCountKey = allDone ? null : PIPELINE_STAGES[nextIdx!].countKey;
  return { stages, allDone, nextLabel, nextCountKey };
}

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const STATUS_DOT_FALLBACK_CLASSES = [
  'bg-violet-500 dark:bg-violet-400',
  'bg-indigo-500 dark:bg-indigo-400',
  'bg-fuchsia-500 dark:bg-fuchsia-400',
  'bg-cyan-500 dark:bg-cyan-400',
  'bg-teal-500 dark:bg-teal-400',
] as const;

function hashStatusLabel(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Tailwind classes for the status dot — semantic colors for common labels, stable hue for anything else. */
function spareCaseStatusDotClass(statusName: string): string {
  const n = statusName.trim().toLowerCase();
  if (!n) return 'bg-slate-400 dark:bg-slate-500';
  if (/\bopen\b/.test(n)) return 'bg-emerald-500 dark:bg-emerald-400';
  if (/\bclose\b/.test(n) || /\bclosed\b/.test(n) || /\bcomplete\b/.test(n)) return 'bg-sky-600 dark:bg-sky-400';
  if (/\bcancel/.test(n)) return 'bg-rose-500 dark:bg-rose-400';
  if (/\bhold\b/.test(n) || /\bpending\b/.test(n) || /\bwait/.test(n)) return 'bg-amber-500 dark:bg-amber-400';
  const idx = hashStatusLabel(n) % STATUS_DOT_FALLBACK_CLASSES.length;
  return STATUS_DOT_FALLBACK_CLASSES[idx];
}

const CategoriesCell: React.FC<{ row: SpareCase }> = ({ row }) => {
  const { colors } = useTheme();
  const labels =
    row.categories && row.categories.length > 0
      ? row.categories
          .map((c) => c.name?.trim() || c.abrv?.trim() || `#${c.id}`)
          .filter(Boolean)
      : row.categoryIds && row.categoryIds.length > 0
        ? row.categoryIds.map((id) => `#${id}`)
        : [];
  const text = labels.join(', ');
  return (
    <div className="max-w-[16rem] py-0.5" title={text || undefined}>
      <span className={cn('text-sm line-clamp-2', colors.text)}>{labels.length ? text : '—'}</span>
    </div>
  );
};

const CaseStatusCell: React.FC<{ row: SpareCase }> = ({ row }) => {
  const statusName = row.status?.name?.trim() || '';
  const dotClass = spareCaseStatusDotClass(statusName);
  return (
    <div className="max-w-[10rem] py-0.5">
      <StatusDotPill
        dotClassName={dotClass}
        title={statusName ? `Case status: ${statusName}` : 'Case status'}
      >
        {statusName || '—'}
      </StatusDotPill>
    </div>
  );
};

/** Single-column combined pipeline: segments + per-stage short labels. */
const CasePipelineCell: React.FC<{ row: SpareCase }> = ({ row }) => {
  const { colors } = useTheme();
  const { stages } = getPipelineStageStates(row._count);
  return (
    <div className="min-w-[14rem] max-w-[22rem] py-0.5">
      <div
        className={cn('rounded-lg border p-1.5', colors.border, 'bg-slate-50/80 dark:bg-white/[0.04]')}
        title="Requisition → RFQ → Quotation → Evaluation → Order → Delivery → Invoice"
      >
        <div className="flex w-full items-end gap-0.5">
          {stages.map((s) => {
            const segmentTitle = `${s.label}: ${s.count} record(s)${s.isNext ? ' — next' : ''}`;
            return (
              <div key={s.countKey} className="flex min-w-0 flex-1 flex-col items-stretch gap-1" title={segmentTitle}>
                <div
                  className={cn(
                    'h-2 w-full min-h-[0.5rem] rounded-sm transition-[height] duration-150',
                    s.hasActivity &&
                      'bg-emerald-500 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.2)] dark:bg-emerald-600',
                    s.isNext &&
                      !s.hasActivity &&
                      'h-2.5 rounded-md bg-gradient-to-b from-amber-300 to-amber-500 shadow-sm ring-2 ring-amber-400/80 ring-offset-1 ring-offset-white dark:from-amber-500 dark:to-amber-600 dark:ring-amber-500/60 dark:ring-offset-slate-900',
                    s.isNotNeeded &&
                      'h-2 rounded-md bg-slate-300/90 ring-1 ring-slate-300/80 dark:bg-slate-700 dark:ring-slate-600/80',
                    !s.hasActivity && !s.isNext && cn(colors.border, 'h-2 bg-white/90 dark:bg-slate-800/90'),
                  )}
                />
                <div
                  className={cn(
                    'w-full select-none text-center text-[0.6rem] font-medium leading-tight sm:text-[0.62rem]',
                    s.hasActivity && 'text-emerald-800 dark:text-emerald-200',
                    s.isNotNeeded && 'text-slate-600 dark:text-slate-300',
                    s.isNext && 'font-semibold text-amber-900 dark:text-amber-200',
                    !s.hasActivity && !s.isNext && colors.textSecondary,
                  )}
                >
                  {s.short}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/** Per-stage counts (narrow columns) — same data as pipeline bar, scannable in the grid. */
const StageCountCell: React.FC<{ row: SpareCase; stageIndex: number }> = ({ row, stageIndex }) => {
  const { colors } = useTheme();
  const { stages } = getPipelineStageStates(row._count);
  const s = stages[stageIndex];
  if (!s) {
    return (
      <span className={cn('text-sm tabular-nums', colors.textSecondary)} title="—">
        —
      </span>
    );
  }
  const title = `${s.label}: ${s.count} record(s)${s.isNext ? ' — next in pipeline' : ''}`;
  return (
    <div
      className={cn(
        'mx-auto inline-flex min-w-[2.25rem] max-w-full flex-col items-center justify-center rounded-md px-1.5 py-1.5',
        s.isNext && 'bg-amber-50 ring-1 ring-amber-400/80 dark:bg-amber-950/35 dark:ring-amber-500/50',
        s.isNotNeeded && 'bg-slate-100 ring-1 ring-slate-300/80 dark:bg-slate-800/70 dark:ring-slate-600/80',
        s.hasActivity && !s.isNext && 'bg-emerald-50/60 ring-1 ring-emerald-500/20 dark:bg-emerald-950/20 dark:ring-emerald-500/25',
      )}
      title={title}
    >
      <span
        className={cn(
          'text-sm font-semibold tabular-nums',
          s.hasActivity && 'text-emerald-800 dark:text-emerald-300',
          s.isNotNeeded && 'text-slate-700 dark:text-slate-300',
          !s.hasActivity && s.isNext && 'text-amber-900 dark:text-amber-200',
          !s.hasActivity && !s.isNext && colors.textSecondary,
        )}
      >
        {s.count}
      </span>
      {s.isNext ? <span className="mt-0.5 text-[0.5rem] font-bold uppercase text-amber-800 dark:text-amber-200">Next</span> : null}
    </div>
  );
};

const CaseNextStepCell: React.FC<{ row: SpareCase }> = ({ row }) => {
  const { colors } = useTheme();
  const { allDone, nextCountKey } = getPipelineStageStates(row._count);
  if (allDone) {
    return (
      <div
        className="max-w-[18rem] py-0.5 text-sm leading-snug text-emerald-800 dark:text-emerald-200"
        title="Each pipeline stage has at least one related record."
      >
        <span className="font-medium">All stages in progress</span>
        <span className={cn('mt-0.5 block text-xs font-normal', colors.textSecondary)}>No empty pipeline step</span>
      </div>
    );
  }
  if (!nextCountKey) {
    return <TextCell value="—" />;
  }
  const text = NEXT_STEP_MESSAGE[nextCountKey];
  return (
    <div
      className={cn('max-w-[18rem] py-0.5 text-sm font-medium leading-snug', colors.text)}
      title={text}
    >
      {text}
    </div>
  );
};

export const moduleConfig = {
  module: {
    title: 'Spare Cases',
    permissionModuleKey: 'spare_cases',
    route: {
      list: '/spares/spare-cases',
      new: '/spares/spare-cases/new',
      detail: (id: number) => `/spares/spare-cases/${id}`,
      edit: (id: number) => `/spares/spare-cases/${id}/edit`,
      caseView: (id: number) => `/spares/spare-cases/${id}/case-view`},
    api: {
      list: '/spare-cases',
      detail: (id: number) => `/spare-cases/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by reference, description or notes...' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['vesselId', 'ref', 'caseDate', 'eta', 'statusId', 'categories']},
  ],
  filters: {
    fields: [
      {
        key: 'vesselId',
        label: 'Vessel',
        type: 'select' as const,
        placeholder: 'All vessels',
        getValue: (item: SpareCase) => item.vessel?.vesselName?.trim() || '',
        options: []},
      {
        key: 'ref',
        label: 'Reference',
        placeholder: 'All references',
        getValue: (item: SpareCase) => item.ref || '',
        options: []},
      {
        key: 'caseDate',
        label: 'Case date',
        type: 'date' as const,
        getValue: (item: SpareCase) => item.caseDate || '',
        options: []},
      {
        key: 'eta',
        label: 'ETA',
        type: 'date' as const,
        getValue: (item: SpareCase) => item.eta || '',
        options: []},
      {
        key: 'statusId',
        label: 'Status',
        type: 'select' as const,
        placeholder: 'All statuses',
        getValue: (item: SpareCase) => item.status?.name?.trim() || '',
        options: []},
      {
        key: 'categories',
        label: 'Categories',
        type: 'select' as const,
        placeholder: 'All categories',
        getValue: (item: SpareCase) =>
          item.categories?.map((c) => c.name?.trim() || c.abrv?.trim() || '').filter(Boolean).join(', ') || '',
        options: []},
    ],
    applyFilter: (
      items: SpareCase[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>,
    ) => {
      const asArray = (v: string[] | string | undefined): string[] =>
        Array.isArray(v) ? v : typeof v === 'string' && v !== '' ? [v] : [];
      const toDateOnly = (s?: string | null): string => (s ? String(s).slice(0, 10) : '');
      return items.filter((item) => {
        const vesselFilters = asArray(filters.vesselId);
        if (vesselFilters.length) {
          const vesselName = (item.vessel?.vesselName || '').toLowerCase();
          if (!vesselFilters.some((f) => vesselName.includes(String(f).toLowerCase()))) return false;
        }

        const refFilters = asArray(filters.ref);
        if (refFilters.length) {
          const v = item.ref || '';
          if (!refFilters.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }

        const caseDateFilter = dateRanges?.caseDate;
        if (caseDateFilter?.start || caseDateFilter?.end) {
          const itemDateRaw = item.caseDate ? new Date(item.caseDate) : null;
          if (!itemDateRaw || Number.isNaN(itemDateRaw.getTime())) return false;
          if (caseDateFilter.start && itemDateRaw < caseDateFilter.start) return false;
          if (caseDateFilter.end && itemDateRaw > caseDateFilter.end) return false;
        } else {
          const caseDateValues = asArray(filters.caseDate);
          if (caseDateValues.length) {
            const itemDate = toDateOnly(item.caseDate);
            if (!caseDateValues.some((f) => itemDate.includes(String(f).slice(0, 10)))) return false;
          }
        }

        const etaFilter = dateRanges?.eta;
        if (etaFilter?.start || etaFilter?.end) {
          const itemDateRaw = item.eta ? new Date(item.eta) : null;
          if (!itemDateRaw || Number.isNaN(itemDateRaw.getTime())) return false;
          if (etaFilter.start && itemDateRaw < etaFilter.start) return false;
          if (etaFilter.end && itemDateRaw > etaFilter.end) return false;
        } else {
          const etaValues = asArray(filters.eta);
          if (etaValues.length) {
            const itemDate = toDateOnly(item.eta);
            if (!etaValues.some((f) => itemDate.includes(String(f).slice(0, 10)))) return false;
          }
        }

        const statusFilters = asArray(filters.statusId);
        if (statusFilters.length) {
          const statusName = (item.status?.name || '').toLowerCase();
          if (!statusFilters.some((f) => statusName === String(f).toLowerCase())) return false;
        }

        const categoryFilters = asArray(filters.categories);
        if (categoryFilters.length) {
          const categoryLabels = (item.categories ?? [])
            .map((c) => c.name?.trim() || c.abrv?.trim() || '')
            .filter(Boolean)
            .map((v) => v.toLowerCase());
          if (!categoryFilters.some((f) => categoryLabels.includes(String(f).toLowerCase()))) return false;
        }
        return true;
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New case',
        showTitle: false,
        variant: 'primary' as const,
        href: '/spares/spare-cases/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: SpareCase) => item.id,
  },
  emptyStates: {
    noData: 'Create a spare case to get started.',
    noSearchResults: 'No spare cases match your search.',
    noFilterResults: 'No spare cases match the filters.',
  },
  delete: {
    title: 'Delete spare case',
    message: 'Delete this spare case?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete spare case.',
    handler: async (id: number) => {
      const { deleteSpareCase } = await import('../../../services/mutations');
      await deleteSpareCase(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This spare case does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Spare Case',
    getTitle: (item: SpareCase) => item.ref || `Spare Case #${item.id}`},
  form: {
    new: { title: 'New spare case', subtitle: 'Create a spare case record', submitText: 'Create' },
    edit: { title: 'Edit spare case', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    /** Open Case View (pipeline) immediately after create so the new case is the active workspace. */
    redirectToCaseViewAfterCreate: true,
    create: async (data: Partial<SpareCase>) => {
      const { createSpareCase } = await import('../../../services/mutations');
      return createSpareCase(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<SpareCase>) => {
      const { updateSpareCase } = await import('../../../services/mutations');
      return updateSpareCase(id, data as Record<string, unknown>);
    },
    prefillNewRecord: async ({
      setMultipleFieldValues,
      getFormData}: {
      setMultipleFieldValues: (updates: Record<string, unknown>) => void;
      getFormData: () => Record<string, unknown>;
    }) => {
      await fillSpareCaseReferenceFromTemplate(setMultipleFieldValues, getFormData);
    }},
  fields: [
    {
      name: 'ref',
      label: 'Reference',
      type: 'text',
      required: false,
      placeholder: 'Filled from template (after date / vessel)',
      validation: { maxLength: 30 }},
    {
      name: 'caseDate',
      label: 'Case date',
      type: 'date',
      required: true,
      validation: { required: true },
      onChangeSideEffect: spareCaseReferencePeekSideEffect},
    { name: 'eta', label: 'ETA', type: 'date' },
    {
      name: 'vesselId',
      label: 'Vessel',
      type: 'select',
      placeholder: 'Select vessel',
      apiEndpoint: '/vessels',
      valueField: 'id',
      labelField: 'vesselName',
      options: [{ value: '', label: 'No vessel' }],
      required: true,
      validation: { required: true },
      onChangeSideEffect: spareCaseReferencePeekSideEffect},
    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      placeholder: 'Select status',
      apiEndpoint: '/spare-statuses',
      valueField: 'id',
      labelField: 'name',
      options: [{ value: '', label: 'No status' }],
      required: true,
      validation: { required: true }},
    {
      name: 'portId',
      label: 'Port',
      type: 'select',
      placeholder: 'Select port',
      apiEndpoint: '/ports',
      valueField: 'id',
      labelField: 'name',
      options: [{ value: '', label: 'No port' }]},
    {
      name: 'categoryIds',
      label: 'Spare categories',
      type: 'multiselect',
      placeholder: 'Select categories',
      apiEndpoint: '/spare-categories',
      valueField: 'id',
      labelField: 'name',
      maxDisplay: 3,
      onChangeSideEffect: spareCaseReferencePeekSideEffect},
    { name: 'description', label: 'Description', type: 'text', placeholder: 'Description', validation: { maxLength: 100 } },
    { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Optional notes' },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'main',
        title: 'Spare Case',
        icon: '',
        gridCols: 'md:grid-cols-12',
        fields: ['caseDate', 'ref', 'vesselId', 'eta', 'description', 'statusId', 'portId', 'categoryIds', 'notes'],
        fieldSpans: {
          ref: 'md:col-span-3',
          caseDate: 'md:col-span-3',
          vesselId: 'md:col-span-3',
          eta: 'md:col-span-3',
          description: 'md:col-span-6',
          statusId: 'md:col-span-3',
          portId: 'md:col-span-3',
          categoryIds: 'md:col-span-12',
          notes: 'md:col-span-12'},
        collapsible: false},
    ]} as FormLayout};

export const getColumns = (formatDate: (dateString?: string) => string): Column<SpareCase>[] => [
  {
    key: 'vesselId',
    header: 'Vessel',
    sortable: true,
    render: (_v: unknown, row: SpareCase) => {
      const name = row?.vessel?.vesselName?.trim();
      const fallback =
        row?.vesselId != null && !row?.vessel ? `Vessel #${row.vesselId}` : undefined;
      return <TextCell value={name || fallback} />;
    }},
  { key: 'ref', header: 'Reference', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'caseDate', header: 'Case Date', sortable: true, render: (v) => formatDate(v) },
  { key: 'eta', header: 'ETA', sortable: true, render: (v) => formatDate(v) },
  {
    key: 'statusId',
    header: 'Status',
    sortable: true,
    render: (_v: unknown, row: SpareCase) => (row ? <CaseStatusCell row={row} /> : <TextCell value="" />)},
  {
    key: 'categories',
    header: 'Categories',
    sortable: false,
    maxWidth: '16rem',
    render: (_v: unknown, row: SpareCase) => (row ? <CategoriesCell row={row} /> : <TextCell value="" />)},
  {
    key: 'pipeline',
    header: 'Pipeline',
    sortable: false,
    maxWidth: '22rem',
    render: (_v: unknown, row: SpareCase) =>
      row ? <CasePipelineCell row={row} /> : <TextCell value="—" />},
  {
    key: 'nextStep',
    header: 'Next step',
    sortable: false,
    maxWidth: '14rem',
    render: (_v: unknown, row: SpareCase) =>
      row ? <CaseNextStepCell row={row} /> : <TextCell value="—" />},
  ...PIPELINE_STAGES.map((st, stageIndex) => ({
    key: `stage_${st.countKey}` as const,
    header: STAGE_COLUMN_HEADER[st.countKey],
    sortable: false,
    align: 'center' as const,
    width: '3.5rem',
    maxWidth: '4.5rem',
    render: (_v: unknown, row: SpareCase) =>
      row ? <StageCountCell row={row} stageIndex={stageIndex} /> : <TextCell value="—" />})),
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
] satisfies Column<SpareCase>[];

export const getDetailSections = (item: SpareCase, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Reference', value: item.ref || '' },
      { label: 'Case date', value: formatDate(item.caseDate || undefined) },
      { label: 'ETA', value: formatDate(item.eta || undefined) },
      {
        label: 'Vessel',
        value: item.vessel?.vesselName?.trim() || (item.vesselId != null ? `Vessel #${item.vesselId}` : '')},
      { label: 'Status', value: item.status?.name?.trim() || (item.statusId != null ? `ID ${item.statusId}` : '') },
      {
        label: 'Spare categories',
        value:
          item.categories?.length ?
            item.categories.map((c) => c.name?.trim() || c.abrv || `#${c.id}`).join(', ')
          : ''},
      { label: 'Port ID', value: item.portId ?? '' },
      { label: 'Description', value: item.description || '' },
      { label: 'Notes', value: item.notes || '' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ]},
];

const TableActionsCell: React.FC<{
  item: SpareCase;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
  returnPath?: string | null;
}> = ({ item, onDelete, canDelete = true, canEdit = true, returnPath }) => {
  const linkState = returnPath ? { from: returnPath } : undefined;
  return (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.caseView(item.id)}
      state={linkState}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="Case activity (custom view)"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        state={linkState}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    )}
  </div>
  );
};

export const getTableActions = (
  item: SpareCase,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean; returnPath?: string | null },
): React.ReactNode => (
  <TableActionsCell
    item={item}
    onDelete={onDelete}
    canDelete={options?.canDelete !== false}
    canEdit={options?.canEdit !== false}
    returnPath={options?.returnPath}
  />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getStatusBadges = () => null;

export const getInitialFormData = (): Partial<SpareCase> => ({
  ref: '',
  caseDate: new Date().toISOString().slice(0, 10),
  eta: '',
  vesselId: undefined,
  statusId: 1,
  portId: undefined,
  categoryIds: [],
  description: '',
  notes: ''});

export const mapItemToFormData = (item: SpareCase): Partial<SpareCase> => ({
  ref: item.ref ?? '',
  caseDate: item.caseDate ? item.caseDate.slice(0, 10) : '',
  eta: item.eta ? item.eta.slice(0, 10) : '',
  vesselId: item.vesselId ?? undefined,
  statusId: item.statusId ?? undefined,
  portId: item.portId ?? undefined,
  categoryIds: [...(item.categoryIds ?? [])],
  description: item.description ?? '',
  notes: item.notes ?? ''});

export type ModuleConfig = typeof moduleConfig;
